// react
import { useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Box, Paper, Stack, Typography } from '@mui/material';

// recharts
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

// utils
import { formatCurrency } from '../../../utils/currency';

// data
import { COLORS } from '../../../data';

// Props

export interface FundingSummaryProps {
	accountBalance?: number;
	currency: string;
	earmarked: number;
	future: number;
	pendingCount: number;
	planned: number;
	total: number;
}

// Colors

const CHART_COLORS = {
	accountBalance: '#e8f5e9', // Light green for total account balance (outer ring)
	accountBalanceStroke: '#4caf50', // Green stroke for outer ring
	earmarked: '#e0e0e0', // Grey for available/earmarked
	future: '#fbbf24', // Yellow for future/pending
	planned: '#1976d2', // Blue for planned
	unallocated: '#f5f5f5', // Very light grey for unallocated portion
};

// Component

export const FundingSummary = ({
	accountBalance = 0,
	currency,
	earmarked,
	future,
	pendingCount,
	planned,
	total,
}: FundingSummaryProps) => {
	const { t } = useTranslation();

	// Calculate chart data for inner ring (allocation breakdown)
	const chartData = useMemo(() => {
		return [
			{ name: t('spendPlan.fundingSummary.earmarked'), value: earmarked, color: CHART_COLORS.planned },
			{ name: t('spendPlan.fundingSummary.future'), value: future, color: CHART_COLORS.future },
		].filter((d) => d.value > 0);
	}, [earmarked, future]);

	// Calculate chart data for outer ring (account balance context)
	const outerChartData = useMemo(() => {
		if (!accountBalance || accountBalance <= 0) return [];
		const allocated = planned > 0 ? planned : earmarked;
		const unallocated = Math.max(0, accountBalance - allocated);
		return [
			{ name: 'Allocated', value: allocated, color: CHART_COLORS.accountBalance },
			{ name: 'Unallocated', value: unallocated, color: CHART_COLORS.unallocated },
		].filter((d) => d.value > 0);
	}, [accountBalance, planned, earmarked]);

	// Calculate progress percentage (earmarked vs planned total)

	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: alpha(COLORS.primary.main, 0.02),
				border: '1px solid',
				borderColor: alpha(COLORS.primary.main, 0.1),
				borderRadius: 2,
				p: 2.5,
				height: '100%',
			}}>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
				<Box
					sx={{
						width: 8,
						height: 8,
						borderRadius: '50%',
						bgcolor: COLORS.success.main,
					}}
				/>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{t('spendPlan.fundingSummary.title')}
				</Typography>
			</Stack>

			{/* Donut Chart */}
			<Box sx={{ position: 'relative', height: 180, mb: 2 }}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						{/* Outer Ring - Account Balance (if provided) */}
						{outerChartData.length > 0 && (
							<Pie
								data={outerChartData}
								cx="50%"
								cy="50%"
								innerRadius={78}
								outerRadius={88}
								paddingAngle={1}
								dataKey="value"
								startAngle={90}
								endAngle={-270}>
								{outerChartData.map((entry, index) => (
									<Cell
										key={`outer-cell-${index}`}
										fill={entry.color}
										stroke={
											entry.name === 'Allocated' ? CHART_COLORS.accountBalanceStroke : '#e0e0e0'
										}
										strokeWidth={1}
									/>
								))}
							</Pie>
						)}
						{/* Inner Ring - Allocation Breakdown */}
						<Pie
							data={chartData}
							cx="50%"
							cy="50%"
							innerRadius={50}
							outerRadius={outerChartData.length > 0 ? 72 : 75}
							paddingAngle={2}
							dataKey="value"
							startAngle={90}
							endAngle={-270}>
							{chartData.map((entry, index) => (
								<Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
							))}
						</Pie>
					</PieChart>
				</ResponsiveContainer>

				{/* Center Text */}
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						textAlign: 'center',
					}}>
					<Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.primary.main }}>
						{formatCurrency({ value: planned, currency, notation: 'compact' })}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{t('planned')}
					</Typography>
				</Box>
			</Box>

			{/* Legend */}
			<Stack spacing={1.5}>
				{/* Earmarked */}
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack direction="row" alignItems="center" spacing={1}>
						<Box
							sx={{
								width: 10,
								height: 10,
								borderRadius: 1,
								bgcolor: CHART_COLORS.earmarked,
							}}
						/>
						<Typography variant="body2" color="text.secondary">
						{t('spendPlan.fundingSummary.earmarked')}
						</Typography>
					</Stack>
					<Typography variant="body2" fontWeight={600}>
						{formatCurrency({ value: earmarked, currency })}
					</Typography>
				</Stack>

				{/* Planned */}
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack direction="row" alignItems="center" spacing={1}>
						<Box
							sx={{
								width: 10,
								height: 10,
								borderRadius: 1,
								bgcolor: CHART_COLORS.planned,
							}}
						/>
						<Typography variant="body2" color="text.secondary">
						{t('planned')}
						</Typography>
					</Stack>
					<Typography variant="body2" fontWeight={600}>
						{formatCurrency({ value: planned, currency })}
					</Typography>
				</Stack>

				{/* Future */}
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack direction="row" alignItems="center" spacing={1}>
						<Box
							sx={{
								width: 10,
								height: 10,
								borderRadius: 1,
								bgcolor: CHART_COLORS.future,
							}}
						/>
						<Typography variant="body2" color="text.secondary">
						{t('spendPlan.fundingSummary.future')}
						</Typography>
					</Stack>
					<Typography variant="body2" fontWeight={600}>
						{formatCurrency({ value: future, currency })}
					</Typography>
				</Stack>

				{/* Divider */}
				<Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 1 }}>
					{/* Account Balance - only show if provided */}
					{accountBalance > 0 && (
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							sx={{ mb: 1 }}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<Box
									sx={{
										width: 10,
										height: 10,
										borderRadius: 1,
										bgcolor: CHART_COLORS.accountBalance,
										border: `1px solid ${CHART_COLORS.accountBalanceStroke}`,
									}}
								/>
								<Typography variant="body2" color="text.secondary">
									Account Balance
								</Typography>
							</Stack>
							<Typography variant="body2" fontWeight={600} color="success.main">
								{formatCurrency({ value: accountBalance, currency })}
							</Typography>
						</Stack>
					)}
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Typography variant="body2" color="text.secondary">
						{t('spendPlan.fundingSummary.totalAllocated')}
						</Typography>
						<Stack alignItems="flex-end">
							<Typography variant="subtitle1" fontWeight={700} color="primary.main">
								{formatCurrency({ value: planned, currency })}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								+ {pendingCount} pending items
							</Typography>
						</Stack>
					</Stack>
				</Box>
			</Stack>
		</Paper>
	);
};
